import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { isWebMode, webLogout } from '../api/client.ts'
import { useMe } from '../api/hooks.ts'
import { Avatar } from '../ui/Avatar.tsx'
import { Logo } from '../ui/EmptyState.tsx'
import {
  IconChart,
  IconCheck,
  IconChevronRight,
  IconClock,
  IconEye,
  IconInfo,
  IconRepeat,
  IconSettings,
  IconShield,
  IconSparkle,
  IconTag,
} from '../ui/icons.tsx'
import { Cell, Section } from '../ui/List.tsx'
import { Screen, ScreenHeader } from '../ui/ScreenHeader.tsx'
import { SkeletonBlock } from '../ui/Skeleton.tsx'

function IconTile({ children }: { children: ReactNode }) {
  return <span className="icon-tile">{children}</span>
}

export function MoreScreen() {
  const me = useMe()
  return (
    <Screen root label="Ещё">
      <ScreenHeader title="Ещё" />
      {me.isPending ? (
        <SkeletonBlock lines={2} />
      ) : me.data ? (
        <Link
          to="/home"
          className="profile profile--link"
          aria-label="Моя страница: заметки, доска задач, сводка дня"
        >
          <Avatar name={me.data.displayName} id={me.data.id} size={52} photo={me.data.photoVersion} />
          <div className="grow">
            <div className="profile__name">{me.data.displayName}</div>
            <div className="hint hint--md">
              {[me.data.position, me.data.department?.name].filter(Boolean).join(' · ') || 'Сотрудник OVO'}
            </div>
            <div className="hint">Моя страница: заметки, доска задач, сводка дня</div>
          </div>
          <IconChevronRight size={20} />
        </Link>
      ) : null}

      <Section id="more-main">
        <Cell
          to="/reports"
          before={
            <IconTile>
              <IconChart size={22} />
            </IconTile>
          }
          title="Отчёт за неделю"
          chevron
        />
        <Cell
          to="/recurring"
          before={
            <IconTile>
              <IconRepeat size={22} />
            </IconTile>
          }
          title="Регулярные задачи"
          chevron
        />
        <Cell
          to="/watching"
          before={
            <IconTile>
              <IconEye size={22} />
            </IconTile>
          }
          title="Наблюдаю"
          chevron
        />
        <Cell
          to="/ideas"
          before={
            <IconTile>
              <IconSparkle size={22} />
            </IconTile>
          }
          title="Ящик идей"
          chevron
        />
        <Cell
          to="/approvals"
          before={
            <IconTile>
              <IconCheck size={22} />
            </IconTile>
          }
          title="Заявки: отпуск, расход, документ"
          chevron
        />
        <Cell
          to="/shifts"
          before={
            <IconTile>
              <IconClock size={22} />
            </IconTile>
          }
          title="Рабочее время"
          chevron
        />
        <Cell
          to="/projects"
          before={
            <IconTile>
              <IconTag size={22} />
            </IconTile>
          }
          title="Проекты и клиенты"
          chevron
        />
      </Section>

      <Section id="more-settings">
        <Cell
          to="/settings"
          before={
            <IconTile>
              <IconSettings size={22} />
            </IconTile>
          }
          title="Настройки"
          subtitle="Часовой пояс, рабочие часы, дайджест"
          chevron
        />
        {me.data?.isAdmin && (
          <Cell
            to="/admin"
            before={
              <IconTile>
                <IconShield size={22} />
              </IconTile>
            }
            title="Админка"
            subtitle="Сотрудники, отделы, приглашения"
            chevron
          />
        )}
        <Cell
          to="/about"
          before={
            <IconTile>
              <IconInfo size={22} />
            </IconTile>
          }
          title="О приложении"
          chevron
        />
      </Section>
      {isWebMode() && (
        <Section id="more-web">
          <Cell onClick={() => void webLogout()} title="Выйти с сайта" subtitle="Только на этом компьютере" />
        </Section>
      )}
    </Screen>
  )
}

export function AboutScreen() {
  return (
    <Screen label="О приложении">
      <ScreenHeader back title="О приложении" small />
      <div className="about">
        <div className="about__logo">
          <Logo size={68} />
        </div>
        <div className="profile__name">OVO Задачи</div>
        <div className="hint hint--md">Версия {__APP_VERSION__}</div>
        <p className="empty__text">
          Задачи компании OVO в Telegram: поставили — исполнитель получил карточку, принял, сделал, вы узнали.
          Напоминаем только когда статус «застыл», без рейтингов и лишнего шума.
        </p>
      </div>
      <Section id="about-rules" title="Как договорились" padded>
        <ul className="rules">
          <li>Нет в боте — нет задачи. Устную просьбу превратите в задачу голосовым за 10 секунд.</li>
          <li>У задачи один исполнитель, срок и понятное «Готово, когда…».</li>
          <li>Не успеваете — предложите новый срок заранее: это нормально.</li>
          <li>Никаких публичных рейтингов.</li>
        </ul>
      </Section>
    </Screen>
  )
}
